import {
  useCallback,
  useRef,
  useState,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
} from 'react';
import { Box } from '../../atoms/Box';
import { Group } from '../../atoms/Group';
import { ActionIcon } from '../../atoms/ActionIcon';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import type { CoreProps } from '../../types';
import { useShouldRender } from '../../../hooks/useShouldRender';
import { ProductImageAvatar } from './ProductImageAvatar';

export type ProductCarouselImage = {
  id?: string;
  fileId?: string;
  url?: string | null;
  isMain?: boolean;
};

export interface ProductImageCarouselProps extends CoreProps {
  images?: ProductCarouselImage[] | null;
  /** Fallback when gallery empty — usually main imageUrl */
  fallbackUrl?: string | null;
  name: string;
  height?: number;
  /** Compact mode for list cards */
  compact?: boolean;
  onImageClick?: (image: ProductCarouselImage, index: number) => void;
  emptySlot?: ReactNode;
}

export function ProductImageCarousel({
  images,
  fallbackUrl,
  name,
  height = 180,
  compact = false,
  onImageClick,
  emptySlot,
  testId,
  view = 'all',
}: ProductImageCarouselProps) {
  const shouldRender = useShouldRender(view);
  const [index, setIndex] = useState(0);
  const touchX = useRef<number | null>(null);

  const slides: ProductCarouselImage[] =
    images && images.length > 0
      ? [...images].sort((a, b) => Number(Boolean(b.isMain)) - Number(Boolean(a.isMain)))
      : fallbackUrl
        ? [{ url: fallbackUrl, isMain: true }]
        : [];

  const safeIndex = slides.length ? Math.min(index, slides.length - 1) : 0;
  const current = slides[safeIndex];

  const go = useCallback(
    (delta: number) => {
      if (slides.length <= 1) return;
      setIndex((i) => (i + delta + slides.length) % slides.length);
    },
    [slides.length],
  );

  if (!shouldRender) return null;

  if (!slides.length) {
    return (
      <Box
        data-testid={testId}
        style={{
          height: compact ? 72 : height,
          borderRadius: compact ? 10 : 'var(--mantine-radius-md)',
          overflow: 'hidden',
          background: 'var(--mantine-color-gray-1)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {emptySlot ?? <ProductImageAvatar name={name} size={compact ? 48 : 72} />}
      </Box>
    );
  }

  const h = compact ? 120 : height;

  return (
    <div
      data-testid={testId}
      style={{
        position: 'relative',
        height: h,
        borderRadius: compact ? 10 : 'var(--mantine-radius-md)',
        overflow: 'hidden',
        background: 'var(--mantine-color-gray-1)',
        touchAction: 'pan-y',
      }}
      onTouchStart={(e: ReactTouchEvent) => {
        touchX.current = e.changedTouches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e: ReactTouchEvent) => {
        if (touchX.current == null) return;
        const x = e.changedTouches[0]?.clientX;
        if (x == null) return;
        const dx = x - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) < 40) return;
        go(dx < 0 ? 1 : -1);
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (current) onImageClick?.(current, safeIndex);
        }}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: onImageClick ? 'pointer' : 'default',
        }}
      >
        {current?.url ? (
          <img
            src={current.url}
            alt={name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{ height: '100%', display: 'grid', placeItems: 'center' }}>
            <ProductImageAvatar name={name} size={compact ? 48 : 72} />
          </div>
        )}
      </button>

      {slides.length > 1 && !compact ? (
        <>
          <ActionIcon
            variant="filled"
            color="dark"
            size="sm"
            aria-label="Previous image"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', opacity: 0.75 }}
          >
            <IconChevronLeft size={16} />
          </ActionIcon>
          <ActionIcon
            variant="filled"
            color="dark"
            size="sm"
            aria-label="Next image"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', opacity: 0.75 }}
          >
            <IconChevronRight size={16} />
          </ActionIcon>
        </>
      ) : null}

      {slides.length > 1 ? (
        <Group
          gap={4}
          justify="center"
          style={{
            position: 'absolute',
            bottom: 8,
            left: 0,
            right: 0,
            pointerEvents: 'none',
          }}
        >
          {slides.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === safeIndex ? 8 : 6,
                height: i === safeIndex ? 8 : 6,
                borderRadius: 99,
                background:
                  i === safeIndex ? 'var(--mantine-color-white)' : 'rgba(255,255,255,0.55)',
                boxShadow: '0 0 2px rgba(0,0,0,0.35)',
              }}
            />
          ))}
        </Group>
      ) : null}
    </div>
  );
}
